import axios from 'axios';
// INV-F2: sessão tem uma única definição. `auth/session.js` não importa nada,
// então não há ciclo com este módulo nem com `authService.js`.
import { clearSession } from '../auth/session';
// Detecção canônica do 403 de cobrança (invariante 4). `planErrors.js` não
// importa nada — mesmo motivo de `auth/session.js`: imune a ciclo com este módulo.
import { isPaymentRequired } from '../utils/planErrors';

// Rede de segurança dos fallbacks (ver INV-F1): se um destes valores chegar a
// ser usado em produção, isso é incidente — não configuração. Justamente por
// isso eles precisam apontar para hosts que existem.
const LOCAL_API_URL = 'http://localhost:3001/api';
// `www` e não o apex, e o fato que decide é um só: **o apex redireciona, o `www`
// não**. O apex `nexustech.api.br` manda para `www.nexustech.api.br` em qualquer
// método — `GET /health` responde 301, `OPTIONS /api/auth/login` (o que o
// preflight de fato vê) responde 307; ambos medidos em 20/08/2026. É o redirect
// que mata a chamada: a spec de CORS **não segue redirect em requisição
// preflighted**, e todo POST/PATCH daqui manda `Content-Type: application/json`,
// logo passa por preflight. Não confira isto por código de sucesso: a resposta
// do `www` varia com a Origin enviada (a allowlist do backend devolve 403 para
// origem não listada). O que não varia é o redirect. `www` é também a forma que
// a ADR-0005 já cita.
const PROD_API_URL = 'https://www.nexustech.api.br/api';

// ✅ Função para detectar ambiente com mais precisão
const getApiUrl = () => {
    const isDev = process.env.NODE_ENV === 'development';
    const isProd = process.env.NODE_ENV === 'production';


    if(isDev){
        const url = process.env.REACT_APP_API_URL_DEV || LOCAL_API_URL;
        return url;
    }

    if (isProd) {
        const url = process.env.REACT_APP_API_PROD || PROD_API_URL;
        return url;
    }

    // Ambiente desconhecido (`NODE_ENV=test`, build fora do CRA): cai no mesmo
    // alvo do ramo de desenvolvimento, que é o único backend alcançável aí.
    // Antes isto era `${process.env.BACKEND_URL}`: sem o prefixo `REACT_APP_` o
    // webpack do CRA nunca injeta a var, então o baseURL virava a **string
    // literal "undefined"** — o Axios aceita sem reclamar e só quebra na
    // primeira chamada. Também não usamos `REACT_APP_BACKEND_URL` (que existe e
    // é lida em `AuthPage.js`): aquela var é a ORIGEM do backend, sem `/api`, e
    // baseURL sem `/api` cai exatamente no 404-com-HTML descrito em INV-F1.
    // Resolve UMA vez: o log tem que anunciar a URL que o Axios vai mesmo usar.
    // Log que diverge do retorno manda quem está debugando conectividade olhar
    // o host errado — é o mesmo defeito que esta função inteira existe para matar.
    const url = process.env.REACT_APP_API_URL_DEV || LOCAL_API_URL;
    console.warn(`⚠️ Ambiente desconhecido, usando URL padrão: ${url}`);
    return url;
};

const API_BASE_URL = getApiUrl();

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// ✅ Interceptor para adicionar token automaticamente
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        console.error('❌ Erro na requisição:', error.message);
        return Promise.reject(error);
    }
);

// ✅ Interceptor para tratar erros de resposta
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.error('❌ Erro na resposta:', error.response?.status, error.message);

        // Tratamento específico por status
        if (error.response?.status === 401) {
            console.warn('⚠️ Não autorizado (401) - Redirecionando para login');
            clearSession();
            // Reload completo: zera o estado React do AuthContext junto com o
            // storage. Ver INV-F2 — storage limpo com `user` ainda em memória
            // seria sessão pela metade.
            window.location.href = '/registration';
        } else if (isPaymentRequired(error)) {
            // 🔴 NÃO desloga e NÃO redireciona — o INVERSO do 401 acima.
            // 401 é "quem é você?"; este é "sua assinatura está pendente". Tirar
            // o dono do app aqui o tiraria justamente da tela onde ele pagaria.
            // Nada de storage: INV-F2 — `clearSession()` é a única definição de
            // encerrar sessão, e encerrar sessão não é o que está acontecendo.
            // O erro segue para o `.catch` da tela chamadora (o
            // `Promise.reject` no fim), que decide a mensagem; o estado global
            // já aparece no banner do AppLayout.
            console.warn('⚠️ Cobranca pendente (403 PAYMENT_REQUIRED) - sessao mantida');
        } else if (error.response?.status === 500) {
            console.error('🔥 Erro interno do servidor (500)');
        } else if (!error.response) {
            console.error('🌐 Erro de rede - Servidor não respondeu');
        }

        return Promise.reject(error);
    }
);

export default api;